import {useMessage} from '@coolcolduk/ui-mui';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router';
import {z} from 'zod';
import {ExamModule, Question, QuestionStatus} from '@cp/model';
import createEmptyQuestionData from '../../../components/QuestionEditForm/utils/createEmptyQuestionData';
import mapQuestionDataToQuestion from '../../../components/QuestionEditForm/utils/mapQuestionDataToQuestion';
import mapQuestionDataToQuestionCheckInput from '../../../components/QuestionEditForm/utils/mapQuestionDataToQuestionCheckInput';
import mapQuestionToQuestionData from '../../../components/QuestionEditForm/utils/mapQuestionToQuestionData';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import {useGenerateQuestionGraphJson} from '../../../hooks/generate/useGenerateQuestionGraphJson';
import {useCheckQuestionWithGpt} from '../../../hooks/question/useCheckQuestionWithGpt';
import {useCheckQuestionWithGptDirect} from '../../../hooks/question/useCheckQuestionWithGptDirect';
import {useCheckQuestionWithRules} from '../../../hooks/question/useCheckQuestionWithRules';
import {useCheckQuestionWithRulesDirect} from '../../../hooks/question/useCheckQuestionWithRulesDirect';
import listQuestionsDoc from '../../../api/question/listQuestionsDoc';
import {ListQuestionsSingleTopicVariables} from '../../../hooks/question/useListQuestions';
import {useUpdateQuestion} from '../../../hooks/question/useUpdateQuestion';
import {QuestionFormData} from '../../../types/QuestionFormData';
import {useSearchParamsHandler} from '../../../hooks/helper/useSearchParamsHandler';

const questionViewParamsSchema = z.object({
  moduleId: z.string().optional(),
  topic: z.string().optional(),
  difficulty: z.number().int().min(1).max(5).optional(),
  status: z.nativeEnum(QuestionStatus).optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).optional(),
});

type QuestionViewParams = z.infer<typeof questionViewParamsSchema>;

export interface QuestionFilters {
  moduleId: string;
  topics: string;
  difficulty: number | null;
  status: QuestionStatus | null;
  offset?: number;
  limit?: number;
}

export interface UseAdminQuestionViewReturn {
  // Data
  question: Question | null;
  examModules: ExamModule[];
  filters: ListQuestionsSingleTopicVariables | null;
  selectedModule: ExamModule | undefined;

  // Loading states
  isLoading: boolean;
  modulesLoading: boolean;
  updateLoading: boolean;
  generateGraphLoading: boolean;
  checkRulesLoading: boolean;
  checkGptLoading: boolean;

  // Form state
  formData: QuestionFormData;
  handleFormDataChange: (data: QuestionFormData) => void;

  // Dialog state
  checkResultDialogOpen: boolean;
  checkResult: Question | null;
  handleCloseCheckResultDialog: () => void;

  // Handlers
  handleBack: () => void;
  handleSave: () => Promise<void>;
  handleCheckWithRules: () => Promise<void>;
  handleCheckWithGpt: () => Promise<void>;
  handleGenerateGraphJson: () => Promise<void>;
  handleNavigateToSeedQuestion: () => void;
}

export default function useAdminQuestionView(): UseAdminQuestionViewReturn {
  const {questionId} = useParams<{questionId: string}>();
  const navigate = useNavigate();
  const location = useLocation();
  const {examModules, loading: modulesLoading} = useListExamModules();
  const {updateQuestion, loading: updateLoading} = useUpdateQuestion();
  const {generateQuestionGraphJson, loading: generateGraphLoading} = useGenerateQuestionGraphJson();
  const {checkQuestionWithRules, loading: checkRulesLoading} = useCheckQuestionWithRules();
  const {checkQuestionWithGpt, loading: checkGptLoading} = useCheckQuestionWithGpt();
  const {checkQuestionWithRulesDirect, loading: checkRulesDirectLoading} = useCheckQuestionWithRulesDirect();
  const {checkQuestionWithGptDirect, loading: checkGptDirectLoading} = useCheckQuestionWithGptDirect();
  const {showMessage} = useMessage();

  const {searchParams} = useSearchParamsHandler<QuestionViewParams>({
    schema: questionViewParamsSchema,
    defaultValues: {
      moduleId: '',
      topic: '',
      difficulty: undefined,
      status: undefined,
      offset: 0,
      limit: 10,
    },
  });

  // Dialog state for check results
  const [checkResultDialogOpen, setCheckResultDialogOpen] = useState(false);
  const [checkResult, setCheckResult] = useState<Question | null>(null);

  // Get question and filters from location state
  const locationState = location.state as
    | {question: Question; filters: ListQuestionsSingleTopicVariables; sessionId?: string}
    | undefined;

  const [question, setQuestion] = useState<Question | null>(locationState?.question || null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);

  // Initialize form data from question
  const [formData, setFormData] = useState(() => {
    if (locationState?.question) {
      return mapQuestionToQuestionData(locationState.question, [], locationState?.filters?.moduleId);
    }
    return createEmptyQuestionData(locationState?.filters?.moduleId || searchParams.moduleId);
  });

  // Filters are read-only, derived from location state or URL search params
  const filters: ListQuestionsSingleTopicVariables | null = useMemo(
    () =>
      locationState?.filters ||
      (searchParams.moduleId
        ? {
            moduleId: searchParams.moduleId,
            topic: searchParams.topic,
            difficulty: searchParams.difficulty,
            status: searchParams.status,
            offset: searchParams.offset,
            limit: searchParams.limit,
          }
        : null),
    [locationState?.filters, searchParams],
  );

  const selectedModule = examModules.find((module) => module.id === filters?.moduleId);

  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);

  // Update form data when question changes
  useEffect(() => {
    if (question) {
      setFormData(mapQuestionToQuestionData(question, examModules, filters?.moduleId));
    } else {
      setFormData(createEmptyQuestionData(filters?.moduleId));
    }
  }, [question, examModules, filters?.moduleId]);

  // Fetch question if not in location state but we have moduleId
  useEffect(() => {
    if (!question && questionId && filters?.moduleId && !hasAttemptedFetch) {
      setHasAttemptedFetch(true);
      setLoadingQuestion(true);
      setFetchLoading(true);
      listQuestionsDoc({
        moduleId: filters.moduleId,
        limit: 1000,
        offset: 0,
      })
        .then((list) => {
          const foundQuestion = list.find((q: Question) => q.id === questionId);
          if (foundQuestion) {
            setQuestion(foundQuestion);
          } else {
            showMessage('Question not found', 6000, 'error');
          }
        })
        .catch(() => {
          showMessage('Failed to load question', 6000, 'error');
        })
        .finally(() => {
          setLoadingQuestion(false);
          setFetchLoading(false);
        });
    }
  }, [question, questionId, filters?.moduleId, hasAttemptedFetch, showMessage]);

  const handleBack = useCallback(() => {
    // If we came from a session page, navigate back to the session
    if (locationState?.sessionId) {
      navigate(`/session/${locationState.sessionId}`);
      return;
    }

    // Navigate back using browser back to preserve filters
    navigate(-1);
  }, [navigate, locationState?.sessionId]);

  const handleSave = useCallback(async () => {
    if (!question) return;

    const questionData = mapQuestionDataToQuestion(formData);
    if (!questionData) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    try {
      const updatedQuestion = await updateQuestion(
        questionData.moduleId,
        question.id,
        questionData.topic,
        questionData.difficulty,
        questionData.questionText,
        questionData.hint,
        questionData.options,
        questionData.answer,
        questionData.explanation,
        questionData.status,
        questionData.statusNote,
        questionData.image,
        questionData.graphJsonString,
      );

      if (updatedQuestion) {
        setQuestion(updatedQuestion);
        showMessage('Question updated successfully', 6000, 'success');
      }
    } catch {
      // Error saving question
      showMessage('Failed to save question. Please try again.', 6000, 'error');
    }
  }, [question, formData, updateQuestion, showMessage]);

  const handleCheckWithRules = useCallback(async () => {
    // If editing an existing question, use the old hook
    if (question && filters?.moduleId) {
      try {
        const updatedQuestion = await checkQuestionWithRules(filters.moduleId, question.id);

        if (updatedQuestion) {
          setQuestion(updatedQuestion);
          // Show dialog with check result
          setCheckResult(updatedQuestion);
          setCheckResultDialogOpen(true);
        }
      } catch {
        // Error checking question with rules
        showMessage('Failed to check question with rules', 6000, 'error');
      }
      return;
    }

    // If creating/editing a new question, use the direct hook
    const questionCheckInput = mapQuestionDataToQuestionCheckInput(formData, examModules);
    if (!questionCheckInput) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    try {
      const result = await checkQuestionWithRulesDirect(questionCheckInput);

      if (result) {
        // Show dialog with check result
        setCheckResult(result);
        setCheckResultDialogOpen(true);
      }
    } catch {
      // Error checking question with rules
      showMessage('Failed to check question with rules', 6000, 'error');
    }
  }, [
    question,
    filters?.moduleId,
    formData,
    examModules,
    checkQuestionWithRules,
    checkQuestionWithRulesDirect,
    showMessage,
  ]);

  const handleCheckWithGpt = useCallback(async () => {
    // If editing an existing question, use the old hook
    if (question && filters?.moduleId) {
      try {
        const updatedQuestion = await checkQuestionWithGpt(filters.moduleId, question.id);

        if (updatedQuestion) {
          setQuestion(updatedQuestion);
          // Show dialog with check result
          setCheckResult(updatedQuestion);
          setCheckResultDialogOpen(true);
        }
      } catch {
        // Error checking question with GPT
        showMessage('Failed to check question with GPT', 6000, 'error');
      }
      return;
    }

    // If creating/editing a new question, use the direct hook
    const questionCheckInput = mapQuestionDataToQuestionCheckInput(formData, examModules);
    if (!questionCheckInput) {
      showMessage('Please fill in all required fields', 6000, 'error');
      return;
    }

    // Get audience level from selected module
    if (!selectedModule) {
      showMessage('Please select a module', 6000, 'error');
      return;
    }

    try {
      const result = await checkQuestionWithGptDirect(questionCheckInput, selectedModule.name);

      if (result) {
        // Show dialog with check result
        setCheckResult(result);
        setCheckResultDialogOpen(true);
      }
    } catch {
      // Error checking question with GPT
      showMessage('Failed to check question with GPT', 6000, 'error');
    }
  }, [
    question,
    filters?.moduleId,
    formData,
    examModules,
    selectedModule,
    checkQuestionWithGpt,
    checkQuestionWithGptDirect,
    showMessage,
  ]);

  const handleGenerateGraphJson = useCallback(async () => {
    if (!question) return;

    try {
      const topic = [question.moduleTopic.category, question.moduleTopic.topic, question.moduleTopic.detail]
        .filter(Boolean)
        .join(' > ');

      const graphJson = await generateQuestionGraphJson(
        question.questionText,
        topic,
        question.difficulty,
        question.hint || undefined,
        question.options,
        question.answer,
        question.explanation,
      );

      if (graphJson) {
        setFormData((prev) => ({...prev, graphJson}));
        showMessage('Graph JSON generated successfully', 6000, 'success');
      } else {
        showMessage('No graph needed for this question', 6000, 'info');
      }
    } catch {
      // Error generating graph JSON
      showMessage('Failed to generate graph JSON. Please try again.', 6000, 'error');
    }
  }, [question, generateQuestionGraphJson, showMessage]);

  const handleFormDataChange = useCallback((data: QuestionFormData) => {
    setFormData(data);
  }, []);

  const handleCloseCheckResultDialog = useCallback(() => {
    setCheckResultDialogOpen(false);
    setCheckResult(null);
  }, []);

  const handleNavigateToSeedQuestion = useCallback(() => {
    if (!question?.seedQuestionId || !filters?.moduleId) return;

    // Navigate to seed question page with moduleId in URL
    navigate(`/admin/seedQuestion/${question.seedQuestionId}?moduleId=${filters.moduleId}`);
  }, [question, filters?.moduleId, navigate]);

  const isLoading = loadingQuestion || fetchLoading;
  const combinedCheckRulesLoading = checkRulesLoading || checkRulesDirectLoading;
  const combinedCheckGptLoading = checkGptLoading || checkGptDirectLoading;

  return {
    question,
    examModules,
    filters,
    selectedModule,
    isLoading,
    modulesLoading,
    updateLoading,
    generateGraphLoading,
    checkRulesLoading: combinedCheckRulesLoading,
    checkGptLoading: combinedCheckGptLoading,
    formData,
    handleFormDataChange,
    checkResultDialogOpen,
    checkResult,
    handleCloseCheckResultDialog,
    handleBack,
    handleSave,
    handleCheckWithRules,
    handleCheckWithGpt,
    handleGenerateGraphJson,
    handleNavigateToSeedQuestion,
  };
}
